<template>
  <v-card class="team-card" flat>
    <div class="team-card__header">
      <div>
        <v-icon class="team-card__header-icon">people</v-icon>
        <span>Operadores</span>
      </div>
      <v-btn color="primary" depressed round small @click="openNew">
        <v-icon left small>person_add</v-icon> Novo operador
      </v-btn>
    </div>

    <div v-if="loading" class="pa-4 text-xs-center">
      <v-progress-circular indeterminate color="primary"></v-progress-circular>
    </div>

    <v-list v-else two-line class="pa-0">
      <template v-for="op in operators">
        <v-divider :key="`d-${op.id}`"></v-divider>
        <v-list-tile :key="op.id">
          <v-list-tile-avatar>
            <v-icon :color="op.owner ? 'primary' : 'grey'">
              {{ op.owner ? 'verified_user' : 'person' }}
            </v-icon>
          </v-list-tile-avatar>
          <v-list-tile-content>
            <v-list-tile-title>
              {{ op.name || op.email }}
              <v-chip v-if="op.owner" x-small label class="ml-1 owner-badge">Dono</v-chip>
            </v-list-tile-title>
            <v-list-tile-sub-title>
              {{ op.email }} · {{ op.profile_name || 'Sem perfil' }}
            </v-list-tile-sub-title>
          </v-list-tile-content>
          <v-list-tile-action v-if="!op.owner">
            <div>
              <v-btn icon small @click="openEdit(op)"><v-icon small>edit</v-icon></v-btn>
              <v-btn icon small @click="remove(op)"><v-icon small>delete</v-icon></v-btn>
            </div>
          </v-list-tile-action>
        </v-list-tile>
      </template>
    </v-list>

    <!-- Create/Edit dialog -->
    <v-dialog v-model="dialog" persistent max-width="480px">
      <v-card class="team-modal">
        <v-card-title class="headline">
          <v-icon color="primary" class="mr-2">person_add</v-icon>
          {{ editing ? 'Editar operador' : 'Novo operador' }}
        </v-card-title>
        <v-card-text>
          <v-text-field label="Nome" v-model="form.name"></v-text-field>
          <v-text-field label="Documento (CPF/CNPJ)" v-model="form.document"></v-text-field>
          <v-text-field label="E-mail" v-model="form.email" :disabled="!!editing" type="email"></v-text-field>
          <v-text-field label="Celular" v-model="form.phone"></v-text-field>
          <v-select
            label="Perfil vinculado"
            v-model="form.account_profile_id"
            :items="profileOptions"
            item-text="name"
            item-value="id"
          ></v-select>
          <div v-if="!editing" class="caption grey--text">
            O operador receberá um e-mail para definir a própria senha.
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn flat @click="dialog = false" :disabled="saving">Cancelar</v-btn>
          <v-btn color="primary" :loading="saving" :disabled="!canSave" @click="save">Salvar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-snackbar v-model="snackbar" :color="snackColor" :timeout="4000">{{ snackText }}</v-snackbar>
  </v-card>
</template>

<script>
import Flowit from "@/services/flowit";

export default {
  name: "OperatorsPanel",
  data() {
    return {
      loading: true,
      saving: false,
      operators: [],
      profileOptions: [],
      dialog: false,
      editing: null,
      form: { name: "", document: "", email: "", phone: "", account_profile_id: null },
      snackbar: false,
      snackText: "",
      snackColor: "success",
    };
  },
  computed: {
    canSave() {
      if (this.editing) return !!this.form.name;
      return !!this.form.name && !!this.form.email && !!this.form.account_profile_id;
    },
  },
  created() {
    this.load();
  },
  methods: {
    notify(text, color = "success") {
      this.snackText = text;
      this.snackColor = color;
      this.snackbar = true;
    },
    load() {
      this.loading = true;
      Promise.all([Flowit.team.listOperators(), Flowit.team.listProfiles()])
        .then(([ops, profs]) => {
          this.operators = ops.data;
          this.profileOptions = profs.data.profiles;
        })
        .catch(() => this.notify("Erro ao carregar operadores", "error"))
        .finally(() => {
          this.loading = false;
        });
    },
    openNew() {
      this.editing = null;
      this.form = { name: "", document: "", email: "", phone: "", account_profile_id: null };
      this.dialog = true;
    },
    openEdit(op) {
      this.editing = op;
      this.form = {
        name: op.name || "",
        document: op.document || "",
        email: op.email,
        phone: op.phone || "",
        account_profile_id: op.account_profile_id,
      };
      this.dialog = true;
    },
    save() {
      this.saving = true;
      const req = this.editing
        ? Flowit.team.updateOperator(this.editing.id, {
            name: this.form.name,
            document: this.form.document,
            phone: this.form.phone,
            account_profile_id: this.form.account_profile_id,
          })
        : Flowit.team.createOperator(this.form);
      req
        .then(() => {
          this.dialog = false;
          this.notify(this.editing ? "Operador atualizado" : "Operador criado — e-mail de acesso enviado");
          this.load();
        })
        .catch((e) => {
          const msg = (e.response && e.response.data && e.response.data.errors) || ["Erro ao salvar operador"];
          this.notify(Array.isArray(msg) ? msg.join(", ") : msg, "error");
        })
        .finally(() => {
          this.saving = false;
        });
    },
    remove(op) {
      if (!window.confirm(`Remover o operador "${op.name || op.email}"?`)) return;
      Flowit.team
        .deleteOperator(op.id)
        .then(() => {
          this.notify("Operador removido");
          this.load();
        })
        .catch(() => this.notify("Erro ao remover operador", "error"));
    },
  },
};
</script>

<style scoped>
.team-card {
  border-radius: 18px !important;
  box-shadow: 0 6px 22px rgba(17, 24, 39, 0.06) !important;
  overflow: hidden;
}
.team-card__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  font-size: 17px;
  font-weight: 700;
  color: #1f2933;
}
.team-card__header > div:first-child {
  display: flex;
  align-items: center;
  gap: 10px;
}
.team-card__header-icon {
  color: var(--primary-color, #1976d2) !important;
}
.owner-badge {
  background: #eef5ff !important;
  color: #1e6cff !important;
}
.team-modal {
  border-radius: 18px !important;
}
</style>
